import { useState } from 'react'
import RenderHTML from '../../../../../../components/ui-components/RenderHTML'
import { convertEpochToDate } from '../../../../../../utils/utils'
import { useWebViewPageVisibility } from '../../../../../../hooks/useWebViewPageVisibility'
import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../utils/appBridge'
import { PlansSipListEventIds, PlansSipListScreenByPlanMode } from '../../../analytics/plansEvents'
import { DisclaimerBlock } from '../../../lumpsum/plans-list/footer/LumpsumPlansFooter'
import { PlansFaqsSection } from '../../../lumpsum/plans-list/footer/PlansFaqsSection'
import { ResetStrategyBanner, RESET_STRATEGY_CHEVRON_SVG } from '../../../lumpsum/plans-list/footer/ResetStrategyBanner'
import { ResetStrategyBottomSheet } from '../../../lumpsum/plans-list/footer/ResetStrategyBottomSheet'
import { useSipPlansContext } from '../SipPlansProvider'
import { SIP_PLANS_FAQS } from './sipPlansFaqs'

const FAQ_PREVIEW_COUNT = 3

export function SipPlansFooter() {
  const { planMode, plansOverview, plansQuery, markTimerResumeRefetch } = useSipPlansContext()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [awaitingResume, setAwaitingResume] = useState(false)

  const lastRefreshText = convertEpochToDate(plansOverview?.planSnapshotDate ?? 0, '%_d %B %Y')
  const analyticsScreen = PlansSipListScreenByPlanMode?.[planMode]
  const resetAllocationCta = plansOverview?.resetAllocation
  const editSipCta = plansOverview?.editSip

  useWebViewPageVisibility({
    enabled: awaitingResume,
    onResume: () => {
      setAwaitingResume(false)
      markTimerResumeRefetch()
      plansQuery.refetch()
    }
  })

  return (
    <div className="bg-[#F0F4F7] px-5 pt-8 pb-12">
      <div className="flex flex-col gap-2">
        {lastRefreshText.length > 0 ? (
          <div className="text-[14px] text-[#6A6E6E]">
            Funds shown as per last refresh on {lastRefreshText}
          </div>
        ) : null}
      </div>

      {editSipCta != null ? (
        <EditExternalSipsRow
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: analyticsScreen,
                eventId: PlansSipListEventIds.EditExternalSips,
                buttonLabel: 'Edit External SIPs'
              }
            })
            // Resume refetch only after the native edit screen is opened.
            setAwaitingResume(true)
            postCTA(JSON.stringify(editSipCta))
          }}
        />
      ) : null}

      {resetAllocationCta != null ? (
        <ResetStrategyBanner
          riskProfile={plansOverview?.riskProfile}
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: analyticsScreen,
                eventId: PlansSipListEventIds.ResetStrategy,
                buttonLabel: 'Reset strategy'
              }
            })
            openBottomSheet(
              <ResetStrategyBottomSheet
                screenName={analyticsScreen}
                onDismiss={closeBottomSheet}
                onConfirm={() => {
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      ScreenName: analyticsScreen,
                      eventId: PlansSipListEventIds.ResetStrategyConfirm,
                      buttonLabel: 'Confirm reset'
                    }
                  })
                  closeBottomSheet()
                  // Resume refetch only after the native reset screen is opened.
                  setAwaitingResume(true)
                  postCTA(JSON.stringify(resetAllocationCta))
                }}
              />
            )
          }}
        />
      ) : null}

      <PlansFaqsSection
        faqs={SIP_PLANS_FAQS}
        maxVisibleFaqs={FAQ_PREVIEW_COUNT}
        screenName={analyticsScreen}
        faqAccordionEventId={PlansSipListEventIds.FaqAccordion}
        faqViewAllEventId={PlansSipListEventIds.FaqViewAll}
      />

      <DisclaimerBlock />
    </div>
  )
}

function EditExternalSipsRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 flex w-full items-center gap-3 rounded-[12px] border-0 bg-white px-4 py-4 text-left"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-[14px] font-[500] leading-[140%] text-[#232323]">Edit External SIPs</span>
        <span className="text-[13px] font-[400] leading-[140%] text-[#6A6E6E] max-w-[90%]">
          Did some changes to the SIPs outside of PowerUp Money? Update it here
        </span>
      </span>
      <RenderHTML innerHTMLText={RESET_STRATEGY_CHEVRON_SVG} />
    </button>
  )
}
